export interface ViewportPoint {
  readonly x: number;
  readonly y: number;
}

export interface ViewportState {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly scale: number;
}

export interface ViewportScaleBounds {
  readonly maxScale: number;
  readonly minScale: number;
}

export interface ViewportConfig {
  readonly bounds?: ViewportScaleBounds;
  readonly defaultState?: Partial<ViewportState>;
}

export interface ViewportWheelInput {
  readonly deltaMode?: number;
  readonly deltaX: number;
  readonly deltaY: number;
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
}

export type ViewportWheelIntent =
  | { readonly type: "pan"; readonly delta: ViewportPoint }
  | { readonly type: "zoom"; readonly factor: number };

export interface ViewportModel {
  readonly bounds: ViewportScaleBounds;
  readonly defaultState: ViewportState;
  clampScale: (scale: number) => number;
  createState: (state?: Partial<ViewportState>) => ViewportState;
  panBy: (state: ViewportState, delta: ViewportPoint) => ViewportState;
  panTo: (state: ViewportState, offset: ViewportPoint) => ViewportState;
  reset: () => ViewportState;
  setScale: (state: ViewportState, scale: number) => ViewportState;
  stageToWorld: (state: ViewportState, point: ViewportPoint) => ViewportPoint;
  worldToStage: (state: ViewportState, point: ViewportPoint) => ViewportPoint;
  zoomAtWorld: (
    state: ViewportState,
    scale: number,
    worldAnchor: ViewportPoint,
    stageAnchor?: ViewportPoint,
  ) => ViewportState;
  zoomTo: (state: ViewportState, scale: number, stageAnchor?: ViewportPoint) => ViewportState;
  zoomBy: (state: ViewportState, factor: number, stageAnchor?: ViewportPoint) => ViewportState;
}

export const DEFAULT_VIEWPORT_BOUNDS = Object.freeze<ViewportScaleBounds>({
  maxScale: 3,
  minScale: 0.5,
});

export const DEFAULT_VIEWPORT_STATE = Object.freeze<ViewportState>({
  offsetX: 0,
  offsetY: 0,
  scale: 1,
});

const ORIGIN: ViewportPoint = Object.freeze({ x: 0, y: 0 });

const WHEEL_LINE_HEIGHT = 16;
const WHEEL_PAGE_HEIGHT = 800;
const WHEEL_REFERENCE_DELTA = 100;
const WHEEL_ZOOM_STEP = Math.log(1.1);
const PINCH_ZOOM_MULTIPLIER = 2;
const MAX_WHEEL_ZOOM_STEP = Math.log(1.5);

const assertFinite = (value: number, name: string) => {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
};

const assertPositive = (value: number, name: string) => {
  assertFinite(value, name);
  if (value <= 0) {
    throw new RangeError(`${name} must be greater than zero`);
  }
};

const validateBounds = (bounds: ViewportScaleBounds) => {
  assertPositive(bounds.minScale, "minScale");
  assertPositive(bounds.maxScale, "maxScale");
  if (bounds.minScale > bounds.maxScale) {
    throw new RangeError("minScale must not exceed maxScale");
  }
};

const validatePoint = (point: ViewportPoint) => {
  assertFinite(point.x, "point.x");
  assertFinite(point.y, "point.y");
};

export const normalizeWheelDelta = (delta: number, deltaMode = 0) => {
  if (!Number.isFinite(delta)) return 0;
  if (deltaMode === 1) return delta * WHEEL_LINE_HEIGHT;
  if (deltaMode === 2) return delta * WHEEL_PAGE_HEIGHT;
  return delta;
};

export const resolveViewportWheelIntent = (
  input: ViewportWheelInput,
): ViewportWheelIntent | null => {
  const deltaX = normalizeWheelDelta(input.deltaX, input.deltaMode);
  const deltaY = normalizeWheelDelta(input.deltaY, input.deltaMode);

  if (input.shiftKey) {
    const scrollDelta = deltaY || deltaX;
    return scrollDelta === 0 ? null : { type: "pan", delta: { x: -scrollDelta, y: 0 } };
  }

  if (!input.ctrlKey && Math.abs(deltaX) > Math.abs(deltaY)) {
    return { type: "pan", delta: { x: -deltaX, y: 0 } };
  }

  if (deltaY === 0) return null;

  const multiplier = input.ctrlKey ? PINCH_ZOOM_MULTIPLIER : 1;
  const exponent = Math.max(
    -MAX_WHEEL_ZOOM_STEP,
    Math.min(MAX_WHEEL_ZOOM_STEP, (deltaY / WHEEL_REFERENCE_DELTA) * WHEEL_ZOOM_STEP * multiplier),
  );

  return { type: "zoom", factor: Math.exp(-exponent) };
};

const clampScale = (scale: number, bounds: ViewportScaleBounds) => {
  assertFinite(scale, "scale");
  return Math.min(bounds.maxScale, Math.max(bounds.minScale, scale));
};

const normalizeState = (state: ViewportState, bounds: ViewportScaleBounds): ViewportState => {
  assertFinite(state.offsetX, "offsetX");
  assertFinite(state.offsetY, "offsetY");

  return {
    offsetX: state.offsetX,
    offsetY: state.offsetY,
    scale: clampScale(state.scale, bounds),
  };
};

const createDefaultState = (config: ViewportConfig, bounds: ViewportScaleBounds): ViewportState => {
  const state = config.defaultState ?? {};

  return normalizeState(
    {
      offsetX: state.offsetX ?? DEFAULT_VIEWPORT_STATE.offsetX,
      offsetY: state.offsetY ?? DEFAULT_VIEWPORT_STATE.offsetY,
      scale: state.scale ?? DEFAULT_VIEWPORT_STATE.scale,
    },
    bounds,
  );
};

export const createViewportModel = (config: ViewportConfig = {}): ViewportModel => {
  const bounds = Object.freeze({ ...(config.bounds ?? DEFAULT_VIEWPORT_BOUNDS) });
  validateBounds(bounds);
  const defaultState = createDefaultState(config, bounds);

  const createState = (state: Partial<ViewportState> = {}): ViewportState =>
    normalizeState(
      {
        offsetX: state.offsetX ?? defaultState.offsetX,
        offsetY: state.offsetY ?? defaultState.offsetY,
        scale: state.scale ?? defaultState.scale,
      },
      bounds,
    );

  const worldToStage = (state: ViewportState, point: ViewportPoint): ViewportPoint => {
    const currentState = normalizeState(state, bounds);
    validatePoint(point);

    return {
      x: point.x * currentState.scale + currentState.offsetX,
      y: point.y * currentState.scale + currentState.offsetY,
    };
  };

  const stageToWorld = (state: ViewportState, point: ViewportPoint): ViewportPoint => {
    const currentState = normalizeState(state, bounds);
    validatePoint(point);

    return {
      x: (point.x - currentState.offsetX) / currentState.scale,
      y: (point.y - currentState.offsetY) / currentState.scale,
    };
  };

  const panTo = (state: ViewportState, offset: ViewportPoint): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(offset);

    return normalizeState(
      {
        offsetX: offset.x,
        offsetY: offset.y,
        scale: currentState.scale,
      },
      bounds,
    );
  };

  const panBy = (state: ViewportState, delta: ViewportPoint): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(delta);

    return normalizeState(
      {
        offsetX: currentState.offsetX + delta.x,
        offsetY: currentState.offsetY + delta.y,
        scale: currentState.scale,
      },
      bounds,
    );
  };

  const setScale = (state: ViewportState, scale: number): ViewportState => {
    const currentState = normalizeState(state, bounds);

    return normalizeState(
      {
        offsetX: currentState.offsetX,
        offsetY: currentState.offsetY,
        scale: clampScale(scale, bounds),
      },
      bounds,
    );
  };

  const zoomAtWorld = (
    state: ViewportState,
    scale: number,
    worldAnchor: ViewportPoint,
    stageAnchor?: ViewportPoint,
  ): ViewportState => {
    const currentState = normalizeState(state, bounds);
    validatePoint(worldAnchor);
    const nextStageAnchor = stageAnchor ?? worldToStage(currentState, worldAnchor);
    validatePoint(nextStageAnchor);
    const nextScale = clampScale(scale, bounds);

    return normalizeState(
      {
        offsetX: nextStageAnchor.x - worldAnchor.x * nextScale,
        offsetY: nextStageAnchor.y - worldAnchor.y * nextScale,
        scale: nextScale,
      },
      bounds,
    );
  };

  const zoomTo = (
    state: ViewportState,
    scale: number,
    stageAnchor: ViewportPoint = ORIGIN,
  ): ViewportState => {
    const currentState = normalizeState(state, bounds);
    const worldAnchor = stageToWorld(currentState, stageAnchor);
    return zoomAtWorld(currentState, scale, worldAnchor, stageAnchor);
  };

  const zoomBy = (
    state: ViewportState,
    factor: number,
    stageAnchor: ViewportPoint = ORIGIN,
  ): ViewportState => {
    assertPositive(factor, "factor");
    const currentState = normalizeState(state, bounds);
    return zoomTo(currentState, currentState.scale * factor, stageAnchor);
  };

  return {
    bounds: Object.freeze({ ...bounds }),
    clampScale: (scale) => clampScale(scale, bounds),
    createState,
    defaultState: Object.freeze({ ...defaultState }),
    panBy,
    panTo,
    reset: () => ({ ...defaultState }),
    setScale,
    stageToWorld,
    worldToStage,
    zoomAtWorld,
    zoomBy,
    zoomTo,
  };
};

export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

/** Room left on each side of the whole wall when it is fitted to the canvas. */
export const FIT_MARGIN_PX = 16;

/**
 * The scale at which the whole world fits inside the stage with a small margin on every
 * side, or null while either size is still unknown (a canvas not yet laid out measures 0).
 */
export const getFitScale = (stage: ViewportSize, world: ViewportSize): number | null => {
  if (!(stage.width > 0 && stage.height > 0 && world.width > 0 && world.height > 0)) return null;
  // A thumbnail-sized canvas keeps most of itself for the wall.
  const margin = Math.min(FIT_MARGIN_PX, stage.width * 0.05, stage.height * 0.05);
  const scale = Math.min(
    (stage.width - margin * 2) / world.width,
    (stage.height - margin * 2) / world.height,
  );
  return Number.isFinite(scale) && scale > 0 ? scale : null;
};

/** The whole world at its fit scale, centred in the stage. */
export const getFittedViewport = (
  stage: ViewportSize,
  world: ViewportSize,
): ViewportState | null => {
  const scale = getFitScale(stage, world);
  if (scale === null) return null;
  return {
    offsetX: (stage.width - world.width * scale) / 2,
    offsetY: (stage.height - world.height * scale) / 2,
    scale,
  };
};
